import { AxiosError } from 'axios'
import React, { ChangeEvent, FormEvent, useState } from 'react'

import { usePostApiAuthRequestPasswordReset } from '@/shared/apis/fastmile/generated/auth/auth'
import { ApiErrorResponse } from '@/shared/apis/fastmile/models/ApiErrorResponse'
import { LoginCard } from '@/shared/components/LoginCard/LoginCard'
import { StyledAlert, StyledForm, StyledLink, StyledLogin, StyledSubmitButton } from '@/shared/components/LoginCard/StyledLogin'
import { TextField } from '@/shared/components/TextField/TextField'
import { Box, CircularProgress, FormControl, FormLabel, InputAdornment, Typography } from '@mui/material'

export type PasswordResetRequestPageProps = {
  //
}

export const PasswordResetRequestPage: React.FC<PasswordResetRequestPageProps> = () => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const { mutate: requestPasswordReset, isPending: isLoading } = usePostApiAuthRequestPasswordReset({
    mutation: {
      onError: (err: AxiosError<ApiErrorResponse>) => {
        if (err.response?.data?.detail) {
          setError(err.response?.data?.detail)
        } else {
          setError(err.message || 'An error occurred while requesting password reset.')
        }
      },
      onSuccess: () => {
        setSuccess(true)
      },
    },
  })

  const onRequestPasswordReset = () => {
    setError('')

    const baseUrl = window.location.origin

    requestPasswordReset({
      data: {
        email: email,
        redirectUrl: `${baseUrl}/account/reset-password`,
      },
    })
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onRequestPasswordReset()
  }

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  return (
    <StyledLogin>
      <LoginCard>
        {error && <StyledAlert severity="error" sx={{ mb: 3 }}>
          {error}
        </StyledAlert>}
        {success ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Password Reset Email Sent
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              If an account exists with the email address you entered, we've sent instructions to reset your password. Please check your email.
            </Typography>
            <StyledLink to="/">
              Return to Login
            </StyledLink>
          </Box>
        ) : (
          <StyledForm onSubmit={onSubmit} >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Forgot your password?
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Enter your email address and we'll send you instructions to reset your password.
            </Typography>
            <FormControl required>
              <FormLabel htmlFor="email">
                Email
              </FormLabel>
              <TextField
                id="email"
                type="email"
                autoFocus
                autoComplete="email"
                aria-describedby="email"
                onChange={handleEmailChange}
                value={email}
                fullWidth
              />
            </FormControl>
            <StyledSubmitButton
              fullWidth
              size="medium"
              type="submit"
              disabled={isLoading || !email}
              loading={isLoading}
              endIcon={isLoading ?
                <InputAdornment position="end">
                  <CircularProgress size="small" />
                </InputAdornment> : undefined
              }
            >
              Send Reset Link
            </StyledSubmitButton>
            <Box mt={2} textAlign="center">
              <StyledLink to="/">
                Back to Login
              </StyledLink>
            </Box>
          </StyledForm>
        )}
      </LoginCard>
    </StyledLogin>
  )
}